import { Alert, Banner } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { IGLiveSlideoutBanner } from '@mm/icons/SlideoutBanners';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { selectPromoterById } from '@mm/state/selectors/promoterSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSupportedWidgets } from '../../../../../common/components/AddWidgetInsert/AddWidgetInsert';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import { DialoguePreview } from '../../../../../modules/DailoguePreview/DialoguePreview';
import {
  MOBILE_SMALL,
  MOBILE_THRESHOLD,
} from '../../../../constants/screenSize';
import { InstagramRespondRadioBtns } from '../../../../features/common/components/IGRespondRadioBtns/InstagramRespondRadioBtns';
import FrequencyCapControl from '../../../../features/startBotPromoters/commentGuards/components/CommentGuardForm/FrequencyCapControl';
import { EmbeddableDialogueEditorWithPreview } from '../../../../features/startBotPromoters/common/StartBotsDialogueEditor/EmbeddableDialogueEditorWithPreview';
import { IG_SUPPORTED_WIDGET_TYPES } from '../../../../features/startBotPromoters/instagramStoryMention/InstagramStoryMentionEditor';
import { PromoterHeader } from '../../components/PromoterHeader/PromoterHeader';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { FreeTrialWarning } from '../../elements/FreeTrialWarning';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './IGLiveSlideoutEditor.module.css';

export interface IGLiveSlideoutEditorProps {
  promoterId: number;
}

export const IGLiveSlideoutEditor = ({
  promoterId,
}: IGLiveSlideoutEditorProps) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const promoter = useSelector(state => selectPromoterById(state, promoterId));
  const page = useSelector(state =>
    pageEntityByIdSelector(state, String(promoter?.pageId)),
  );
  const [promoters, setPromoters] = useState<PromoterModel[]>([]);
  const { screenWidth } = useResolutionInfo();
  const isMobile =
    screenWidth <= MOBILE_THRESHOLD && screenWidth > MOBILE_SMALL;
  const pageCRUDActions = usePageCRUDActions();
  const widgetCRUDActions = useWidgetCRUDActions();
  const promoterCRUDActions = usePromoterCRUDActions();
  const { setRef } = useSlideoutOnClose();
  const { allWidgets: allSupportedWidgets } = useSupportedWidgets();
  const widgetsToExclude = allSupportedWidgets
    .filter(w => !IG_SUPPORTED_WIDGET_TYPES.includes(w.type))
    .map(w => w.type);

  useEffect(() => {
    async function fetchItems() {
      const response = await promoterCRUDActions.retrieveAll(
        chatbotId,
        'instagram_live_comment_guard',
      );
      setPromoters(response);
    }
    fetchItems();
  }, [chatbotId, promoterCRUDActions]);

  useEffect(() => {
    (async () => {
      if (page) {
        await Promise.all([
          pageCRUDActions.retrieveById(page.id),
          widgetCRUDActions.retrieveAll(page.id, true),
        ]);
      }
    })();
  }, [page?.id]);

  if (!promoter || !page) {
    return null;
  }

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <PromoterHeader promoter={promoter} title="Instagram Live" />

      <FreeTrialWarning />

      <SlideoutMenu.Content className={styles.content}>
        {!isMobile && (
          <Banner
            css={{
              collapsedHeader: styles.collapsedHeader,
              content: styles.bannerContent,
            }}
            withBannerHeader
            headerText="What is an Instagram Live Autoresponder?"
            withLeftArea={false}
            showCloseIcon
            usedInSlideout
          >
            <IGLiveSlideoutBanner textColor="white" />
          </Banner>
        )}
        <div className={styles.container}>
          <div className={styles.configArea}>
            <CollapsibleContentContainer
              orderNumber="1"
              containerName="Choose how to respond"
              className={styles.collapsibleContainer}
              childrenContainerClassName={styles.collapsibleContainer}
              stepperStylingPlatform="instagram"
              useCircleNumberStyle
            >
              <Alert
                type="warning"
                showCloseIcon={false}
                css={{ container: styles.alert }}
              >
                Сomments on live video will only be sent while video being
                broadcast.
              </Alert>
              <InstagramRespondRadioBtns
                promoter={promoter}
                promoters={promoters}
                type="ig_live"
                css={{
                  radioInputLabel: styles.igRadioInputLabel,
                  textLink: styles.textLink,
                }}
                showInfoIcon
                keywordInfoIconColor="#262626"
                customKeywordBroadPosition="bottom"
                customKeywordBroadAlign={screenWidth > 900 ? 'center' : 'end'}
                customRespondToAllLabel="All comments"
                customRespondKeywordsLabel="Only to comments that contain the following keywords"
                customRespondEmojisLabel="Respond to popular emojis"
              />
            </CollapsibleContentContainer>
            <CollapsibleContentContainer
              orderNumber="2"
              containerName="Reply Instantly to users who comment on your Instagram Live"
              className={styles.collapsibleContainer}
              stepperStylingPlatform="instagram"
              useCircleNumberStyle
            >
              <EmbeddableDialogueEditorWithPreview
                editPaneClassname={styles.dialogueEditPane}
                previewClassName={styles.dialogueEditPreviewPane}
                dialogueId={page.id}
                widgetsToExclude={widgetsToExclude}
                firstWidgetNotAllowedWarning={{
                  showWarning: true,
                  message: `This widget type is NOT supported as the first message in your Instagram Live.
                 Please use a Quick Question widget instead.`,
                  startbotType: 'instagram_cg',
                }}
                platforms={['instagram']}
                disableFirstWidgetConditionalLogic
                usedInSlideout
                hideDialogueSelector
                forInstagram
              />
            </CollapsibleContentContainer>
            <CollapsibleContentContainer
              orderNumber="3"
              containerName="Specify a Frequency Cap"
              className={styles.collapsibleContainerLast}
              stepperStylingPlatform="instagram"
              useCircleNumberStyle
            >
              <FrequencyCapControl
                guard={promoter}
                chatbotId={chatbotId}
                usedInSlideout
              />
            </CollapsibleContentContainer>
          </div>
          {!isMobile && (
            <DialoguePreview
              dialogueId={page.id}
              className={styles.dialoguePreview}
              sticky
            />
          )}
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
